import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Cpu, Gamepad2, Home, ChefHat, Dumbbell, TreePine, Sparkles, Wifi, Grid2x2, Car, Router, Armchair, Dices, ToyBrick, Camera, PawPrint, Sprout, Wrench, ShieldCheck } from 'lucide-react';

interface Category {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  count: number;
}

interface CategoryStyle {
  Icon: React.ElementType;
  color: string;
  bg: string;
}

const STYLES: Record<string, CategoryStyle> = {
  electronics:  { Icon: Cpu,      color: '#ea580c', bg: '#fff7ed' },
  gaming:       { Icon: Gamepad2, color: '#7c3aed', bg: '#f5f3ff' },
  home:         { Icon: Home,     color: '#0284c7', bg: '#f0f9ff' },
  kitchen:      { Icon: ChefHat,  color: '#059669', bg: '#f0fdf4' },
  fitness:      { Icon: Dumbbell, color: '#dc2626', bg: '#fef2f2' },
  outdoor:      { Icon: TreePine, color: '#16a34a', bg: '#f0fdf4' },
  beauty:       { Icon: Sparkles, color: '#db2777', bg: '#fdf2f8' },
  'smart-home': { Icon: Wifi,     color: '#4f46e5', bg: '#eef2ff' },
  automotive:   { Icon: Car,        color: '#475569', bg: '#f8fafc' },
  networking:   { Icon: Router,     color: '#0891b2', bg: '#ecfeff' },
  furniture:    { Icon: Armchair,   color: '#92400e', bg: '#fffbeb' },
  games:        { Icon: Dices,      color: '#9333ea', bg: '#faf5ff' },
  toys:         { Icon: ToyBrick,   color: '#e11d48', bg: '#fff1f2' },
  photography:  { Icon: Camera,     color: '#1e293b', bg: '#f1f5f9' },
  pets:         { Icon: PawPrint,   color: '#ca8a04', bg: '#fefce8' },
  garden:       { Icon: Sprout,     color: '#15803d', bg: '#f0fdf4' },
  tools:        { Icon: Wrench,     color: '#78350f', bg: '#fef3c7' },
  safety:       { Icon: ShieldCheck, color: '#0d9488', bg: '#f0fdfa' },
};

const DEFAULT: CategoryStyle = { Icon: Grid2x2, color: '#64748b', bg: '#f8fafc' };

function getStyle(slug: string): CategoryStyle {
  return STYLES[slug?.toLowerCase()] || DEFAULT;
}

export default function CategoryGrid() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase.from('categories').select('id,slug,name,icon,color,count').order('sort_order').then(({ data, error: err }) => {
      if (cancelled) return;
      if (err) setError(true);
      else if (data) setCategories(data);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) { setError(true); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center text-ink-400 text-sm">Loading...</div>
    </section>
  );

  if (error) return null;

  return (
    <section id="categories" className="py-16 md:py-20 bg-white border-b border-ink-100 reveal">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 tracking-tight">
              Shop by category
            </h2>
            <p className="mt-2 text-ink-500 text-sm">
            {categories.length} categories · {categories.reduce((a, c) => a + (c.count || 0), 0).toLocaleString()} products reviewed
            </p>
          </div>
          <Link to="/search" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-ink-600 hover:text-cta-600 transition whitespace-nowrap">
            Browse all <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {categories.map((c) => {
            const { Icon, color, bg } = getStyle(c.slug);
            return (
              <Link
                key={c.id}
                to={`/search?category=${c.slug}`}
                className="group flex items-center gap-3 p-4 rounded-xl border border-ink-100 bg-white hover:border-ink-200 hover:shadow-soft transition duration-200"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition duration-200 group-hover:scale-105"
                  style={{ background: bg, color }}
                >
                  <Icon size={20} strokeWidth={1.75} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-ink-900 text-sm truncate">{c.name}</p>
                </div>
                <i className="ri-arrow-right-line text-ink-300 group-hover:text-ink-500 ml-auto flex-shrink-0 transition text-sm"></i>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
